/**
 *
 * TermsDialog Component
 *
 */

import React, { useState, useEffect } from "react";
import classnames from "classnames";
import { Modal, Button, stylize } from "@envestnet/envreact-component-library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./styles";
import { DefaultProps, TermsDialogProps } from "./types";

function Dialog(props: TermsDialogProps) {
  const {
    classes,
    style,
    className,
    open,
    buttons,
    closeOnOutsideClick,
    icon,
    title,
    description,
    children,
    onClose,
    isSticky,
    styleObject,
    enableButtonDisabled = false,
  } = props;

  const listInnerRef = React.useRef<HTMLInputElement>(null);
  const [reachedBottom, setReachedBottom] = useState(false);
  const onScroll = () => {
    if (enableButtonDisabled) {
      if (listInnerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;

        if (Math.round(scrollTop + clientHeight) === Math.trunc(scrollHeight)) {
          setReachedBottom(true);
        } else if (scrollTop === 0) {
          setReachedBottom(false);
        }
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      const doc: any = document.getElementById("dialog-body");
      if (doc && doc.scrollHeight < 680) {
        setReachedBottom(true);
      }
    }, 20);
  }, []);

  const leftButtons = buttons?.filter(
    (button) => button.buttonDirection === "LEFT"
  );
  const rightButtons = buttons?.filter(
    (button) => button.buttonDirection !== "LEFT"
  );

  const getButtons = (buttonsArray) =>
    buttonsArray.map((button) => {
      const buttonProps: any = {};
      buttonProps.key = button.id;
      buttonProps.buttonStyle = button.buttonType;
      buttonProps.name = button.title;
      buttonProps.disabled = button.disabled;
      buttonProps.loading = button.loading;
      buttonProps.onClick = button.onClick;
      if (button.icon) {
        const buttonIcon = (
          <FontAwesomeIcon
            icon={button.icon.icon}
            className={button.icon.className}
            style={button.icon.style}
          />
        );
        if (button.title) {
          buttonProps.iconLeft = buttonIcon;
        } else {
          buttonProps.iconOnly = buttonIcon;
        }
      }
      return (
        <Button
          {...buttonProps}
          className={classnames(classes.buttonGap, "header-text-font-family")}
          aria-label={button.icon?.label || button?.label}
        />
      );
    });

  const Buttons = React.useMemo(() => {
    if (buttons) {
      return (
        <div className={classes.buttonsContainer}>
          <div>{getButtons(leftButtons)}</div>
          <div>{getButtons(rightButtons)}</div>
        </div>
      );
    }
    return null;
  }, [buttons, onClose, reachedBottom]);

  return (
    <Modal
      open={open}
      disableBackdropClickClose={!closeOnOutsideClick}
      contentContainerStyle={
        style ? { ...style, borderRadius: 0 } : { borderRadius: 0 }
      }
      contentRootContainerClass={classnames(
        classes.container,
        isSticky ? classes.stickyHeader : "",
        className,
        styleObject?.container
      )}
      onClose={onClose}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon.icon}
          className={icon.className}
          style={icon.style}
          title={icon.label}
        />
      )}
      <div className={classnames(classes.modalHeader, styleObject?.header)}>
        {title && (
          <div
            data-testid="dialog-title"
            className={classnames(
              classes.title,
              styleObject?.title,
              "header-font-family"
            )}
          >
            {title}
          </div>
        )}
      </div>
      <div
        data-testid="dialog-body"
        id="dialog-body"
        className={classnames(classes.modalBody, styleObject?.body)}
        onScroll={() => onScroll()}
        ref={listInnerRef}
      >
        {description && (
          <div
            className={classnames(
              classes.description,
              styleObject?.description,
              "header-text-font-family"
            )}
          >
            {description}
          </div>
        )}
        {children && (
          <div
            className={classnames(
              classes.childrenContainer,
              styleObject?.childrenContainer,
              "header-text-font-family"
            )}
          >
            {children}
          </div>
        )}
      </div>
      <div
        data-testid="dialog-footer"
        className={classnames(classes.modalFooter, styleObject?.footer)}
      >
        {Buttons}
      </div>
    </Modal>
  );
}

export default stylize<TermsDialogProps, DefaultProps>(styles)(Dialog);
